import { FormEvent } from "react"
import { useForm } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { ProgramSelect, type ProgramOption } from "@/components/ProgramSelect"

type DeckFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  action: string
  method: "post" | "patch"
  title: string
  description?: string
  submitLabel: string
  initial?: { title: string; description: string | null }
  /** Official content only: the programs the new record can be filed under. */
  programs?: ProgramOption[]
}

export function DeckFormDialog({
  open,
  onOpenChange,
  action,
  method,
  title,
  description,
  submitLabel,
  initial,
  programs = [],
}: DeckFormDialogProps) {
  const intl = useIntl()
  const form = useForm({
    title: initial?.title ?? "",
    description: initial?.description ?? "",
    program_id: String(programs[0]?.id ?? ""),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.submit(method, action, {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
        if (method === "post") form.reset()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <label htmlFor="deck-title">{intl.formatMessage({ id: "flashcards.deck_title" })}</label>
              <Input
                id="deck-title"
                required
                autoFocus
                aria-invalid={!!form.errors.title}
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
              />
              {form.errors.title && (
                <p className="text-xs text-danger-display">{form.errors.title}</p>
              )}
            </div>
            <ProgramSelect
              id="deck-program"
              programs={programs}
              value={form.data.program_id}
              onChange={(value) => form.setData("program_id", value)}
              error={(form.errors as Record<string, string>).program}
            />
            <div className="space-y-2">
              <label htmlFor="deck-description">{intl.formatMessage({ id: "flashcards.deck_description" })}</label>
              <textarea
                id="deck-description"
                className="form-control form-control-textarea"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {intl.formatMessage({ id: "common.cancel" })}
            </Button>
            <Button type="submit" disabled={form.processing}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
